import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with sensible defaults
const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30s timeout — prevents app freezing on slow/failed requests
});

// Flag to prevent multiple refresh requests
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth header
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const currentToken = localStorage.getItem('token');
      
      if (!currentToken) {
        // No token — clear state and go to landing page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(`${API}/api/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });

        const { token: newToken, user } = response.data;
        
        // Save new token and user
        localStorage.setItem('token', newToken);
        localStorage.setItem('user', JSON.stringify(user));
        
        // Update the authorization header
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        processQueue(null, newToken);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Refresh failed, clear storage and redirect to landing page
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Network errors (server unreachable, timeout, etc.)
    if (!error.response) {
      // Don't redirect on network errors — just reject so the caller can handle it
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// Function to proactively refresh token if it's getting old
export const checkAndRefreshToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) return;

  try {
    // Decode token to check expiry (without verifying signature)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    const timeUntilExpiry = exp - now;
    
    // If token expires in less than 24 hours, refresh it. We issue 30-day
    // tokens server-side, so this rolls the expiry forward on each daily
    // visit — therapist never sees a logout unless they go 30 days without
    // opening the app.
    if (timeUntilExpiry < 24 * 60 * 60 * 1000) {
      const response = await axios.post(`${API}/api/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { token: newToken, user } = response.data;
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(user));
      
      return newToken;
    }
  } catch (error) {
    console.error('Token refresh check failed:', error);
  }
  
  return token;
};

// Set up periodic token refresh check (every 30 minutes)
let refreshInterval = null;

export const startTokenRefreshInterval = () => {
  if (refreshInterval) return;
  
  // Check immediately
  checkAndRefreshToken();
  
  // Then check every 30 minutes
  refreshInterval = setInterval(checkAndRefreshToken, 30 * 60 * 1000);
};

export const stopTokenRefreshInterval = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

export default api;
